import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { UserResponseDto } from '../dto/response/user-response.dto';
import userService from '../services/user.service';
// import { UserSaveRequestDto } from '../dto/request/user-save-request.dto';
import { UserUpdateRequestDto } from '../dto/request/user-update-request.dto';
import { AxiosError } from 'axios';
import { showError, showSuccess } from '../../../../utils/Notifications';

const initialState = {
  status: 'idle',
  loggedInUserRoles: [] as string[],
  users: null as UserResponseDto[] | null,
  user: null as UserResponseDto | null,
  error: null as string | null,
};

export const fetchUsers = createAsyncThunk('users/fetchUsers', async () => {
  return await userService.fetchUsers();
});

export const fetchSoftDeletedUsers = createAsyncThunk(
  'users/fetchSoftDeletedUsers',
  async () => {
    return await userService.fetchSoftDeletedUsers();
  }
);

export const fetchLoggedInUserRoles = createAsyncThunk(
  'users/getLoggedInUserRoles',
  async () => {
    return await userService.fetchLoggedInUserRoles();
  }
);

export const fetchUserById = createAsyncThunk(
  'users/fetchUserById',
  async (id: string) => {
    return await userService.fetchUserById(id);
  }
);

// export const createUser = createAsyncThunk(
//   'users/createUser',
//   async (data: UserSaveRequestDto) => {
//     return await userService.createUser(data);
//   }
// );

export const updateUser = createAsyncThunk(
  'users/updateUser',
  async (payload: { id: string; user: UserUpdateRequestDto }) => {
    const { id, user } = payload;
    return await userService.updateUser(id, user);
  }
);

export const deleteUser = createAsyncThunk(
  'users/deleteUser',
  async (id: string, { rejectWithValue, dispatch }) => {
    try {
      await userService.deleteUser(id);
      dispatch(fetchUsers());
    } catch (e) {
      return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
    }
  }
);

const userSlice = createSlice({
  name: 'userSlice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.users = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(fetchLoggedInUserRoles.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchLoggedInUserRoles.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.loggedInUserRoles = action.payload;
      })
      .addCase(fetchLoggedInUserRoles.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(fetchSoftDeletedUsers.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchSoftDeletedUsers.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.users = action.payload;
      })
      .addCase(fetchSoftDeletedUsers.rejected, (state, action) => {
        state.status = 'failed';
        showError(`Fetch inactive users failed. ${action.payload as string}`);
        state.error = action.error?.message ?? null;
      })
      .addCase(fetchUserById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = action.payload;
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.status = 'failed';
        showError(`Fetch user failed. ${action.payload as string}`);
        state.error = action.error?.message ?? null;
      })
      // .addCase(createUser.pending, (state) => {
      //   state.status = 'loading';
      // })
      // .addCase(createUser.fulfilled, (state, action) => {
      //   state.status = 'succeeded';
      //   showSuccess(`User created successfully.`);
      //   state.users?.push(action.payload);
      // })
      // .addCase(createUser.rejected, (state, action) => {
      //   state.status = 'failed';
      //   showError(`Create user failed. ${action.payload as string}`);
      //   state.error = action.error?.message ?? null;
      // })
      .addCase(updateUser.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        showSuccess(`User updated successfully.`);
        if (state.users) {
          const userIndex = state.users.findIndex(
            (user) => user.id === action.payload.id
          );
          if (userIndex !== -1) {
            state.users[userIndex] = action.payload;
          }
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.status = 'failed';
        showError(`Update user failed. ${action.payload as string}`);
        state.error = action.error?.message ?? null;
      })
      .addCase(deleteUser.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteUser.fulfilled, (state) => {
        state.status = 'succeeded';
        showSuccess('User deleted successfully.');
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
        showError(`Deleting user failed. ${action.payload as string}`);
      });
  },
});

export default userSlice.reducer;
